import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

const Dashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [taskSubmissions, setTaskSubmissions] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const fetchUserInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to view submissions');
        navigate('/login');
        return;
      }

      const response = await axios.get(API_ENDPOINTS.ME, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserInfo(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Failed to fetch user information');
        console.error('Error fetching user info:', error);
      }
    }
  }, [navigate]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to view submissions');
        navigate('/login');
        return;
      }

      const response = await axios.get(API_ENDPOINTS.SUBMISSIONS, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSubmissions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Failed to fetch submissions. Please try to log in first.');
        console.error('Error fetching submissions:', error);
      }
    }
  }, [navigate]);

  const fetchTaskSubmissions = useCallback(async (taskName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.SUBMISSIONS_TASK}/${taskName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTaskSubmissions(prev => ({
        ...prev,
        [taskName]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching submissions for task ${taskName}:`, error);
      setTaskSubmissions(prev => ({
        ...prev,
        [taskName]: []
      }));
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserInfo();
      await fetchSubmissions();
      setLoading(false);
    };
    initializeData();
  }, [fetchUserInfo, fetchSubmissions]);

  useEffect(() => {
    if (userInfo && userInfo.roles) {
      // Fetch submissions for each task the user is a captain for
      userInfo.roles.forEach(role => {
        if (role.role_type === 'task_captain') {
          fetchTaskSubmissions(role.task_name);
        }
      });
    }
  }, [userInfo, fetchTaskSubmissions]);

  const handleViewSubmission = (id) => {
    navigate(`/submissions/${id}`);
  };

  const handleViewTaskSubmission = (taskName, id) => {
    navigate(`/submissions/${id}`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getTaskCaptainRoles = () => {
    if (!userInfo || !userInfo.roles) return [];
    return userInfo.roles.filter(role => role.role_type === 'task_captain');
  };

  const taskCaptainRoles = getTaskCaptainRoles();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/submissions/new')}
        >
          New Submission
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {error}
            </Typography>
            {(error.includes('session has expired') || error.includes('must be logged in')) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ mt: 1 }}
              >
                Go to Login
              </Button>
            )}
          </Box>
        </Alert>
      )}

      {/* Show tabs if user has task captain roles */}
      {taskCaptainRoles.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="My Submissions" />
            {taskCaptainRoles.map((role, index) => (
              <Tab 
                key={role.task_name} 
                label={`${role.task_name} Submissions`}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* My Submissions Tab */}
      {activeTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell>{submission.name}</TableCell>
                  <TableCell>{submission.task_name}</TableCell>
                  <TableCell>{submission.year}</TableCell>
                  <TableCell>
                    {new Date(submission.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewSubmission(submission.id)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {submissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No submissions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Task Captain Tabs */}
      {taskCaptainRoles.map((role, roleIndex) => {
        const taskName = role.task_name;
        const taskSubs = taskSubmissions[taskName] || [];
        const tabIndex = roleIndex + 1;

        if (activeTab === tabIndex) {
          return (
            <TableContainer key={taskName} component={Paper}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  {taskName} Submissions
                </Typography>
                <Chip 
                  label="Task Captain" 
                  color="primary" 
                  size="small" 
                  variant="outlined"
                />
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskSubs.map((submission) => (
                    <TableRow key={submission.id}>
                      <TableCell>{submission.name}</TableCell>
                      <TableCell>User ID: {submission.owner_id}</TableCell>
                      <TableCell>{submission.year}</TableCell>
                      <TableCell>
                        {new Date(submission.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewTaskSubmission(taskName, submission.id)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {taskSubs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No submissions found for this task
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }
        return null;
      })}
    </Container>
  );
};

export default Dashboard; 